import React, { useEffect, useState } from "react";
import axios from "axios";
import { Landmark, Info, Loader2, TrendingUp, TrendingDown, Minus, BellRing, Settings2, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Sparkline({ series, width = 220, height = 48 }) {
  if (!series || series.length < 2) return null;
  const vals = series.map((p) => p.comm_net);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const step = width / (vals.length - 1);
  const pts = vals.map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / range) * height).toFixed(1)}`);
  const last = vals[vals.length - 1];
  const first = vals[0];
  const color = last >= first ? "#2e7d32" : "#b03a2e";
  const zeroY = min < 0 && max > 0 ? height - ((0 - min) / range) * height : null;
  return (
    <svg width={width} height={height} className="overflow-visible">
      {zeroY != null && <line x1="0" y1={zeroY} x2={width} y2={zeroY} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" strokeWidth="1" />}
      <polyline points={pts.join(" ")} fill="none" stroke={color} strokeWidth="1.5" />
      <circle cx={width} cy={height - ((last - min) / range) * height} r="2.5" fill={color} />
    </svg>
  );
}

function fmtNet(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  const s = abs >= 1000 ? `${(abs / 1000).toFixed(1)}k` : `${abs}`;
  return `${n < 0 ? "-" : "+"}${s}`;
}

export default function CotDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [config, setConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cot/config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const toggleMarket = (sym) => {
    setConfig((c) => ({
      ...c,
      markets: c.markets.includes(sym) ? c.markets.filter((m) => m !== sym) : [...c.markets, sym],
    }));
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/cot/config`, { markets: config.markets, threshold: config.threshold });
      setConfig((c) => ({ ...c, ...r.data }));
      toast.success("Préférences d'alerte COT enregistrées");
      setShowConfig(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally { setSaving(false); }
  };

  const checkExtremes = async () => {
    setChecking(true);
    try {
      const r = await axios.post(`${API}/cot/check-extremes?force=true`);
      const n = (r.data.extremes || []).length;
      if (n === 0) {
        toast.info("Aucun positionnement extrême cette semaine (percentile 95/5 sur ~3 ans)");
      } else {
        const sent = r.data.sent || {};
        const via = [sent.telegram && "Telegram", sent.email && "Email"].filter(Boolean).join(" + ");
        toast.success(`${n} extrême(s) détecté(s)${via ? ` — alerte envoyée via ${via}` : " — configurez Telegram/Email dans Paramètres pour recevoir l'alerte"}`);
      }
    } catch {
      toast.error("Vérification impossible");
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/cot/overview`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const markets = (data?.markets || []).filter((m) => m.available);
  const unavailable = (data?.markets || []).filter((m) => !m.available);

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2" data-testid="cot-title">
          <Landmark size={22} className="text-accent-blue" /> COT Hebdo — Positions des Commercials
        </h1>
        <p className="text-white/50 text-sm mt-1">
          Positions nettes des Commercials (banques & hedgers) sur les futures CFTC — évolution 6 mois.
        </p>
      </div>

      <div className="flex justify-end gap-1.5 -mt-2">
        <button
          onClick={() => setShowConfig((v) => !v)}
          className={`btn text-xs flex items-center gap-1.5 ${showConfig ? "border-accent-blue text-accent-blue" : ""}`}
          data-testid="cot-config-btn"
        >
          <Settings2 size={12} /> Alertes personnalisées
        </button>
        <button
          onClick={checkExtremes}
          disabled={checking}
          className="btn text-xs flex items-center gap-1.5 border-warn/40 text-warn hover:bg-warn/10"
          data-testid="cot-check-extremes-btn"
          title="Vérification automatique chaque samedi (après publication CFTC) — ce bouton force une vérification immédiate"
        >
          {checking ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />}
          Vérifier les extrêmes maintenant
        </button>
      </div>

      {showConfig && config && (
        <div className="panel p-4 space-y-3" data-testid="cot-config-panel">
          <div className="text-xs font-semibold uppercase tracking-widest text-white/60">Alertes COT personnalisées</div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Seuil de percentile (sur ~3 ans)</div>
            <div className="flex gap-1.5">
              {[90, 95, 99].map((t) => (
                <button
                  key={t}
                  onClick={() => setConfig((c) => ({ ...c, threshold: t }))}
                  className={`chip text-xs ${config.threshold === t ? "chip-up" : "hover:bg-white/5"}`}
                  data-testid={`cot-threshold-${t}`}
                >
                  ≥{t} / ≤{100 - t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">
              Marchés suivis ({config.markets.length}/{(config.available_markets || []).length})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(config.available_markets || []).map((sym) => {
                const on = config.markets.includes(sym);
                return (
                  <button
                    key={sym}
                    onClick={() => toggleMarket(sym)}
                    className={`chip text-xs ${on ? "chip-up" : "opacity-50 hover:opacity-80"}`}
                    data-testid={`cot-market-${sym}`}
                  >
                    {on && <Check size={10} />} {sym}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-[10px] text-white/40">
              L&apos;alerte hebdomadaire du samedi ne portera que sur les marchés suivis, au seuil choisi.
            </div>
            <button onClick={saveConfig} disabled={saving || config.markets.length === 0} className="btn btn-primary text-xs disabled:opacity-40" data-testid="cot-config-save">
              {saving ? <Loader2 size={12} className="animate-spin" /> : "Enregistrer"}
            </button>
          </div>
        </div>
      )}

      <div className="panel p-3 flex items-start gap-3 text-xs text-warn/90 border-warn/30" data-testid="cot-delay-banner">
        <Info size={14} className="shrink-0 mt-0.5" />
        <div>
          <b>Données différées :</b> {data?.delay_warning || "Données CFTC publiées le vendredi pour le mardi précédent (~3 jours de retard). Biais hebdomadaire, PAS un signal temps réel."}
          {" "}Une alerte automatique est envoyée chaque <b>samedi</b> si un marché atteint un positionnement
          extrême (percentile ≥95 ou ≤5 sur ~3 ans) — configurez Telegram/Email dans les Paramètres.
        </div>
      </div>

      {loading && (
        <div className="grid place-items-center py-20 text-white/40"><Loader2 className="animate-spin" /></div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="cot-grid">
        {markets.map((m) => {
          const BiasIcon = m.bias === "bullish" ? TrendingUp : m.bias === "bearish" ? TrendingDown : Minus;
          const biasColor = m.bias === "bullish" ? "text-up" : m.bias === "bearish" ? "text-down" : "text-white/50";
          const biasLabel = m.bias === "bullish" ? "Acheteur" : m.bias === "bearish" ? "Vendeur" : "Neutre";
          const thr = config?.threshold || 95;
          const followed = !config || config.markets.includes(m.symbol);
          const ext = m.percentile == null ? null : m.percentile >= thr ? "high" : m.percentile <= 100 - thr ? "low" : null;
          return (
            <div key={m.symbol} className="panel p-4" data-testid={`cot-card-${m.symbol}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-heading font-semibold flex items-center gap-2">
                  {m.symbol}
                  {ext && (
                    <span
                      className={`text-[9px] font-bold px-1.5 py-0.5 rounded animate-pulse ${ext === "high" ? "bg-up/20 text-up border border-up/40" : "bg-down/20 text-down border border-down/40"}`}
                      title={`Positionnement Commercials au percentile ${m.percentile}% sur ~3 ans (${m.history_weeks} semaines) — seuil ${thr}`}
                      data-testid={`cot-extreme-${m.symbol}`}
                    >
                      ⚡ EXTRÊME {ext === "high" ? "HAUT" : "BAS"}
                    </span>
                  )}
                  {!followed && (
                    <span className="text-[9px] text-white/30 border border-white/10 rounded px-1 py-0.5" title="Non inclus dans vos alertes hebdomadaires" data-testid={`cot-unfollowed-${m.symbol}`}>
                      🔕 non suivi
                    </span>
                  )}
                </div>
                <span className={`chip ${m.bias === "bullish" ? "chip-up" : m.bias === "bearish" ? "chip-down" : "chip-neutral"}`}>
                  <BiasIcon size={11} /> {biasLabel}
                </span>
              </div>
              <div className="text-[10px] text-white/40 truncate mb-2" title={m.market}>{m.market}</div>
              <Sparkline series={m.series} />
              <div className="flex items-center justify-between mt-2 text-xs">
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-white/40">Net actuel</div>
                  <div className={`font-mono ${m.latest_net >= 0 ? "text-up" : "text-down"}`}>{fmtNet(m.latest_net)}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-white/40">Δ semaine</div>
                  <div className={`font-mono ${biasColor}`}>{fmtNet(m.latest_change)}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-white/40">Percentile 3 ans</div>
                  <div className={`font-mono ${ext ? (ext === "high" ? "text-up" : "text-down") : "text-white/60"}`}>{m.percentile != null ? `${m.percentile}%` : "—"}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-white/40">Rapport</div>
                  <div className="font-mono text-white/60">{m.report_date}</div>
                </div>
              </div>
              {m.inverted_for_pair && (
                <div className="text-[9px] text-white/35 mt-1.5">Signe inversé (USD devise de base sur la paire)</div>
              )}
            </div>
          );
        })}
      </div>

      {!loading && unavailable.length > 0 && (
        <div className="text-[11px] text-white/40" data-testid="cot-unavailable">
          Indisponibles : {unavailable.map((m) => m.symbol).join(", ")}
        </div>
      )}

      <div className="text-[10px] text-white/35 leading-relaxed border-t border-white/5 pt-3">
        Source : CFTC Legacy Futures-Only Report (publicreporting.cftc.gov), gratuit. Les Commercials sont
        les acteurs qui se couvrent (hedgers) — une augmentation massive de leurs positions nettes est souvent
        contra-cyclique. Ces données ne constituent pas un conseil en investissement.
      </div>
    </div>
  );
}
